/**
 * App icon mapping: maps app short_name to a local SVG logo.
 *
 */

import googleAds from "@/assets/settings/app-logos/google-ads.svg";
import googleDrive from "@/assets/settings/app-logos/google-drive.svg";
import googleDocs from "@/assets/settings/app-logos/google-docs.svg";
import googleSheets from "@/assets/settings/app-logos/google-sheets.svg";
import googleSearchConsole from "@/assets/settings/app-logos/google-search-console.svg";
import googleAnalytics from "@/assets/settings/app-logos/google-analytics.svg";
import googleChat from "@/assets/settings/app-logos/google-chat.svg";
import hubspot from "@/assets/settings/app-logos/hubspot.svg";
import slack from "@/assets/settings/app-logos/slack.svg";
import linkedin from "@/assets/settings/app-logos/linkedin.svg";
import meta from "@/assets/settings/app-logos/meta.svg";
import monday from "@/assets/settings/app-logos/monday.svg";
import intercom from "@/assets/settings/app-logos/intercom.svg";
import zoho from "@/assets/settings/app-logos/zoho.svg";
import outreach from "@/assets/settings/app-logos/outreach.svg";
import calendly from "@/assets/settings/app-logos/calendly.svg";
import webflow from "@/assets/settings/app-logos/webflow.svg";
import wordpress from "@/assets/settings/app-logos/wordpress.svg";
import apify from "@/assets/settings/app-logos/apify.svg";
import ahrefs from "@/assets/settings/app-logos/ahrefs.svg";
import factors from "@/assets/settings/app-logos/factors.svg";
import semrush from "@/assets/settings/app-logos/semrush.svg";
import salesforce from "@/assets/settings/app-logos/salesforce.svg";
import strivelabs from "@/assets/settings/app-logos/strivelabs.svg";

const APP_LOGO_MAP: Record<string, string> = {
  // Google products
  google_ads: googleAds,
  google_drive: googleDrive,
  google_docs: googleDocs,
  google_sheets: googleSheets,
  google_search_console: googleSearchConsole,
  google_analytics: googleAnalytics,
  google_ads_dataset: googleAds,
  google_chat: googleChat,

  // Other brands
  hubspot,
  slack,
  linkedin,
  meta_ads: meta,
  monday,
  intercom,
  zoho_books: zoho,
  outreach,
  calendly,
  webflow,
  wordpress,
  apify,
  ahrefs,
  factors,
  semrush,
  salesforce,
  // Both Salesforce connections share the brand mark. Registering
  // `salesforce_pardot_dataset` also keeps `resolveAppShortName`'s longest-prefix match
  // from resolving its tool ids to the shorter `salesforce`.
  salesforce_pardot_dataset: salesforce,

  // StriveLabs itself — used for warehouse-backed attribution on generated
  // inbox cards (tools_used / why_recommendation sources).
  strivelabs,
};

/**
 * Returns the logo URL for an app by short_name.
 * fallback initial-letter avatar in that case.
 */
export function getAppLogoUrl(shortName: string): string {
  return APP_LOGO_MAP[shortName] ?? "";
}

// Longest slug first so `google_ads_dataset` wins over `google_ads`.
const APP_SHORT_NAMES_BY_LENGTH = Object.keys(APP_LOGO_MAP).sort((a, b) => b.length - a.length);

/**
 * Best-effort app short_name behind a pinned tool name. Chat tool ids are
 * `${app_short_name}_${action}` (see `deriveChatToolId` in workflow-apps) and
 * raw provider actions arrive upper-cased ("GOOGLE_ANALYTICS_RUN_REPORT"), so a
 * longest-prefix match over the known slugs recovers the app.
 *
 * @returns The app short_name, or null when no known app matches.
 */
export function resolveAppShortName(toolName: string): string | null {
  const normalized = toolName.trim().toLowerCase();
  return APP_SHORT_NAMES_BY_LENGTH.find((shortName) => normalized.startsWith(shortName)) ?? null;
}

/** "google_search_console" → "Google Search Console". */
export function humanizeAppShortName(shortName: string): string {
  return shortName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
